import type { SimEvent } from "../protocol/events";
import { addComposition, contains, purityOf, species, totalUnits } from "./composition";
import { applyHeatwaveHeat, applySeismicPressure, dispatchScriptedEvents } from "./facility";
import {
  accumulateCatalystPoisoning,
  accumulateContamination,
  accumulateHeat,
  accumulatePressure,
  accumulateResidence,
  accumulateVermin,
  isRigDead,
  resolveFailures,
} from "./hazards";
import { processHoldTransforms } from "./hold";
import { applyLeakLosses, processMovement } from "./movement";
import { processPhaseConversion } from "./phase";
import { processReactions } from "./reactions";
import { processSeparation } from "./separation";
import { AMBIENT_TEMP_C, newPacket, resolveInfestationGate, resolveSludgeGate, type EngineState } from "./state";
import { processThermalRegulation } from "./thermal";

/**
 * ONE TICK. The order below is fixed and load-bearing — change it and hazard
 * timing changes with it, so it is documented here and asserted by the
 * golden-run test.
 *
 *   1. Inputs emit packets
 *   2. Node processing (reactions, temperature deltas, phase conversion)
 *   3. Movement along edges
 *   4. Backlog / over-pressure evaluation
 *   5. Hazard counters (heat, corrosion, residence, catalyst)
 *   6. Failure resolution (explode / burst / leak)
 *   7. Objective check
 *
 * Nodes and edges are always iterated in stable id order. The RNG *is* consulted,
 * in exactly two places — `pollute` picks where a hazard card lands in the draw
 * pile, and §3.3's vermin pick chooses which node the rodents stop — so a tick is
 * a pure function of the state going in, seeded RNG included.
 */
export function runTick(state: EngineState): SimEvent[] {
  const events: SimEvent[] = [];

  if (state.isRunComplete || state.isRunFailed) return events;

  // --- 1. Inputs emit -----------------------------------------------------
  // Captured before dispatch runs: a freeze `dispatchScriptedEvents` sets
  // fresh this tick must survive to the end of THIS tick's step 1 (so
  // movement, in step 3, sees it) and only be consumed at the start of the
  // NEXT tick — an unconditional decrement here would zero it out before
  // step 3 ever ran.
  const hadPriorFreeze = state.powerFreezeTicks > 0;
  const { seismicFiredThisTick } = dispatchScriptedEvents(state, events);

  const sortedNodes = [...state.nodes].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  for (const node of sortedNodes) {
    if (node.isDestroyed || node.kind !== "inputTank" || !node.emits) continue;
    if (node.buffer.length >= node.bufferCapacity) continue;

    // §3 Precursor Purity Injection: Budget adds its authored impurities and
    // is contaminated from birth; Premium emits the feed as-is, gated on its
    // supply cap (spec 0027). A sourced tank with no contract chosen yet
    // emits nothing — the three cases are mutually exclusive, not a
    // Budget/else split, or an unchosen tank would wrongly fall into Premium.
    const temperature = node.feedTemperature ?? AMBIENT_TEMP_C;
    if (node.sourcingOptions !== null && node.precursor === null) {
      continue;
    } else if (node.precursor?.tier === "budget") {
      const composition = addComposition(node.emits, node.precursor.impurities ?? {});
      node.buffer.push(newPacket(state, composition, { isContaminated: true, temperature }));
    } else {
      if (node.supplyRemaining !== null && node.supplyRemaining <= 0) continue;
      node.buffer.push(newPacket(state, { ...node.emits }, { temperature }));
      if (node.supplyRemaining !== null) {
        node.supplyRemaining = Math.max(0, node.supplyRemaining - totalUnits(node.emits));
        if (node.supplyRemaining <= 0) {
          events.push({
            type: "feed.exhausted",
            nodeId: node.id,
            emittedUnits: node.sourcingOptions!.premium.supplyCap!,
          });
        }
      }
    }
  }

  if (hadPriorFreeze) state.powerFreezeTicks = Math.max(0, state.powerFreezeTicks - 1);

  // --- 2. Node processing -------------------------------------------------
  for (const node of sortedNodes) {
    if (node.isDestroyed) continue;
    // Resolved once per node per tick and shared with steps 3 and 5
    // (`03-hazards.md` §2.5) — a sludged node's reaction, phase conversion
    // and push all run on the same every-other-tick cadence.
    resolveSludgeGate(node);
    // Spec 0033 §3.3: the infestation gate, resolved the same way — sludge
    // is pinned inactive while infested (one debuff per node, decision 11),
    // so exactly one of the two ever actually throttles a given node.
    resolveInfestationGate(state, node);
    processReactions(state, node, events);
    processPhaseConversion(state, node, events);
    processSeparation(state, node, events);
    processThermalRegulation(state, node, events);
    processHoldTransforms(state, node, events);
  }

  // --- 3. Movement --------------------------------------------------------
  const { blockedEdgeIds } = processMovement(state, events);
  applyLeakLosses(state, events);

  // --- 4. Backlog / over-pressure ----------------------------------------
  accumulatePressure(state, blockedEdgeIds, events);
  // After the reset, not before: a flowing line zeroes its counter this tick,
  // and a quake's +1 applied first would be wiped before anything read it
  // (G-G12). Landing last, it leaves every filled pipe one stall closer to a burst.
  applySeismicPressure(state, seismicFiredThisTick, events);

  // --- 5. Hazard counters -------------------------------------------------
  // Corrosion is charged during movement (it depends on what passed through).
  applyHeatwaveHeat(state, events);
  accumulateHeat(state, events);
  accumulateCatalystPoisoning(state, events);
  accumulateResidence(state, events);
  accumulateContamination(state, events);
  accumulateVermin(state, events);

  // --- 6. Failure resolution ---------------------------------------------
  resolveFailures(state, events);

  // --- 7. Objective check -------------------------------------------------
  collectAtHoppers(state, events);
  evaluateRunEnd(state, events);

  // Spec 0033 §3.3: the starve-out checkpoint — an infested node that held
  // any packet at the end of THIS tick counts toward keeping the infestation
  // alive, alongside the Run-phase-entry checkpoint (phases.ts). `||=`, not a
  // plain assignment: one empty tick must not erase an earlier full one from
  // the same turn.
  for (const node of state.nodes) {
    if (node.infestation === null) continue;
    node.infestation.heldPacketThisTurn ||= node.buffer.length >= 1;
  }

  return events;
}

/**
 * Product landing in a hopper counts toward quota, gated on purity: a packet
 * below the stage's minPurity is collected but contributes nothing, which is
 * how `02-cards-and-economy.md` §5's Target Yield and Purity Rate are scored.
 */
function collectAtHoppers(state: EngineState, events: SimEvent[]): void {
  let progressed = false;

  for (const node of state.nodes) {
    if (node.isDestroyed || node.kind !== "productHopper" || node.buffer.length === 0) continue;

    for (const packet of node.buffer) {
      const purity = purityOf(packet.composition);
      const arrived = totalUnits(packet.composition);

      if (purity < state.stage.objective.minPurity) {
        // It reached the hopper and counted for nothing: waste, and the score
        // says so (`02-cards-and-economy.md` §5 grades on purity, not just yield).
        state.wastedUnits += arrived;
        continue;
      }

      // Only species the objective actually asks for score.
      const target = state.stage.objective.targetComposition;
      const scoring: Record<string, number> = {};
      for (const key of species(packet.composition)) {
        if ((target[key] ?? 0) > 0) scoring[key] = packet.composition[key] ?? 0;
      }
      const scoringUnits = totalUnits(scoring);
      if (scoringUnits === 0) {
        state.wastedUnits += arrived;
        continue;
      }

      state.collected = addComposition(state.collected, scoring);
      state.scoredUnits += scoringUnits;
      state.purityUnitsWeighted += purity * scoringUnits;
      // Byproduct riding along in the same packet is off-spec material.
      state.wastedUnits += arrived - scoringUnits;
      progressed = true;
    }

    node.buffer = [];
  }

  if (progressed) {
    events.push({
      type: "objective.progressed",
      collected: { ...state.collected },
      targetComposition: { ...state.stage.objective.targetComposition },
    });
  }

  // Spec 0023: a wasteBuyer collects too, but scores nothing — no
  // `objective.progressed` event, and the tally lands in its own field so it
  // never touches `collected`/`scoredUnits`/`wastedUnits`. Spec 0026 reports
  // this as `RunTallies.valorizedUnits`; `@reagent/meta` is what prices it.
  for (const node of state.nodes) {
    if (node.isDestroyed || node.kind !== "wasteBuyer" || node.buffer.length === 0) continue;

    for (const packet of node.buffer) {
      state.valorizedUnits += totalUnits(packet.composition);
    }

    node.buffer = [];
  }

  // Spec 0032: a vent collects too, but pays nothing and scores nothing — it
  // never touches `collected`/`scoredUnits`/`wastedUnits`/`valorizedUnits`,
  // and its tally lands in its own field. Vents in id order, since the event
  // fires per vent rather than once for the whole pass like `objective.progressed`.
  const sortedVents = [...state.nodes].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  for (const node of sortedVents) {
    if (node.isDestroyed || node.kind !== "vent" || node.buffer.length === 0) continue;

    let units = 0;
    for (const packet of node.buffer) {
      units += totalUnits(packet.composition);
    }

    state.ventedUnits += units;
    if (units >= 1) {
      events.push({ type: "node.vented", nodeId: node.id, units });
    }

    node.buffer = [];
  }
}

function evaluateRunEnd(state: EngineState, events: SimEvent[]): void {
  if (state.isRunComplete || state.isRunFailed) return;

  if (contains(state.collected, state.stage.objective.targetComposition)) {
    state.isRunComplete = true;
    events.push({ type: "run.cleared" });
    return;
  }

  if (isRigDead(state)) {
    state.isRunFailed = true;
    state.failureReason = "explosion";
    events.push({ type: "run.failed", reason: "explosion" });
  }
}

/** Exposed for the status phase: quota missed once the turn budget runs out. */
export function failForQuotaMissed(state: EngineState, events: SimEvent[]): void {
  if (state.isRunComplete || state.isRunFailed) return;
  state.isRunFailed = true;
  state.failureReason = "quotaMissed";
  events.push({ type: "run.failed", reason: "quotaMissed" });
}
